import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI. Receives the error. */
  fallback?: (error: Error) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches unhandled render errors so they don't
 * blank the entire page. Displays a minimal recovery UI by default.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error);
      return (
        <div style={{ padding: "2rem", textAlign: "center", fontFamily: "sans-serif" }}>
          <h2 style={{ marginBottom: "0.5rem" }}>Something went wrong</h2>
          <p style={{ color: "#666", marginBottom: "1.5rem" }}>
            An unexpected error occurred. Please refresh the page or contact support.
          </p>
          <button
            onClick={() => this.setState({ error: null })}
            style={{
              marginRight: "0.75rem",
              padding: "0.5rem 1.25rem",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "0.5rem 1.25rem", cursor: "pointer" }}
          >
            Reload page
          </button>
          {import.meta.env.DEV && (
            <pre
              style={{
                marginTop: "1.5rem",
                textAlign: "left",
                background: "#f4f4f4",
                padding: "1rem",
                borderRadius: "4px",
                overflow: "auto",
                fontSize: "0.8rem",
              }}
            >
              {error.stack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
